import { FixedSizeList } from 'react-window';
import { components, GroupBase, MenuListProps } from 'react-select';

/**
 * Drop-in `MenuList` for react-select that virtualizes its options with
 * react-window. react-select renders every filtered option to the DOM, which
 * makes large option sets (e.g. the full Encompass writable-field schema) janky
 * to open and to type into. This renders only the rows in view, so performance
 * stays flat regardless of option count.
 *
 * Assumes single-line options of fixed height — pair it with an `option` style
 * that clips overflow (nowrap + ellipsis) so rows can't wrap past ITEM_HEIGHT.
 */
const ITEM_HEIGHT = 35;

export function VirtualizedMenuList<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: MenuListProps<Option, IsMulti, Group>) {
  const { options, children, maxHeight, getValue } = props;

  // Non-array children = react-select's loading / "no options" placeholder.
  if (!Array.isArray(children)) {
    return <components.MenuList {...props}>{children}</components.MenuList>;
  }

  const selected = getValue()[0];
  const selectedIndex = selected ? options.indexOf(selected) : -1;
  const height = Math.min(maxHeight, children.length * ITEM_HEIGHT);

  return (
    <FixedSizeList
      width="100%"
      height={height}
      itemCount={children.length}
      itemSize={ITEM_HEIGHT}
      initialScrollOffset={Math.max(selectedIndex, 0) * ITEM_HEIGHT}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </FixedSizeList>
  );
}

export default VirtualizedMenuList;
